import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as THREE from 'three';
import { FikoKaliciOlcumler } from '../../FikoKaliciOlcumler';
import { FikoThreeJsComponent } from '../../../fiko-three-js.component';

/**
 * Kalıcı ölçümleri yönetmek için dialog component'i
 * - Ölçüm listesi görüntüleme
 * - Ölçüm silme
 * - Ölçüm görünürlüğü toggle
 * - Ölçüm rengi değiştirme
 * - Toplu işlemler
 */
@Component({
  selector: 'fikoyt-kalici-olcumler-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './kalici-olcumler-dialog.component.html',
  styleUrls: ['./kalici-olcumler-dialog.component.scss']
})
export class KaliciOlcumlerDialogComponent implements OnInit, OnDestroy {

  @Input() kaliciOlcumler: FikoKaliciOlcumler;
  @Input() anaGosterim: FikoThreeJsComponent;
  @Output() kapandi = new EventEmitter<void>();

  // Ölçüm listesi
  olcumListesi: OlcumItem[] = [];

  // Seçili ölçümler
  seciliOlcumler: Set<string> = new Set();

  // Filtre
  aramaMetni: string = '';

  // Sıralama
  siralama: 'tarih' | 'isim' | 'deger' = 'tarih';
  siralamaYonu: 'asc' | 'desc' = 'desc';

  constructor(public CD: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.olcumleriYukle();
  }

  ngOnDestroy(): void {
    // Cleanup
  }

  /**
   * Kalıcı ölçümleri yükle ve listeye dönüştür
   */
  olcumleriYukle(): void {
    if (!this.kaliciOlcumler) return;

    this.olcumListesi = [];

    this.kaliciOlcumler.children.forEach((child) => {
      if (child instanceof THREE.Group) {
        const userData = child.userData;

        this.olcumListesi.push({
          uuid: child.uuid,
          isim: child.name || 'İsimsiz Ölçüm',
          baslik: userData?.olcumBaslik || userData?.FikoArayuz?.baslik || '',
          tarih: userData?.olusturmaTarihi || new Date(),
          gorunur: child.visible,
          renk: this.olcumRenginiAl(child),
          grup: child
        });
      }
    });

    // Sırala
    this.sirala();
    this.CD.detectChanges();
  }

  /**
   * Ölçümün ana rengini al
   */
  private olcumRenginiAl(grup: THREE.Group): string {
    let renk = '#22c55e'; // Varsayılan yeşil

    grup.traverse((child) => {
      if (child instanceof THREE.Line) {
        const material = child.material as THREE.LineBasicMaterial;
        if (material && material.color) {
          renk = '#' + material.color.getHexString();
          return;
        }
      }
    });

    return renk;
  }

  /**
   * Listeyi filtrele
   */
  get filtrelenmisListe(): OlcumItem[] {
    if (!this.aramaMetni.trim()) {
      return this.olcumListesi;
    }

    const aranan = this.aramaMetni.toLowerCase();
    return this.olcumListesi.filter(item =>
      item.isim.toLowerCase().includes(aranan) ||
      item.baslik.toLowerCase().includes(aranan)
    );
  }

  /**
   * Listeyi sırala
   */
  sirala(): void {
    this.olcumListesi.sort((a, b) => {
      let sonuc = 0;

      switch (this.siralama) {
        case 'tarih':
          sonuc = new Date(a.tarih).getTime() - new Date(b.tarih).getTime();
          break;
        case 'isim':
          sonuc = a.isim.localeCompare(b.isim);
          break;
        case 'deger':
          sonuc = a.baslik.localeCompare(b.baslik);
          break;
      }

      return this.siralamaYonu === 'asc' ? sonuc : -sonuc;
    });
  }

  /**
   * Sıralama değiştir
   */
  siralamaDeistir(yeniSiralama: 'tarih' | 'isim' | 'deger'): void {
    if (this.siralama === yeniSiralama) {
      this.siralamaYonu = this.siralamaYonu === 'asc' ? 'desc' : 'asc';
    } else {
      this.siralama = yeniSiralama;
      this.siralamaYonu = 'asc';
    }
    this.sirala();
    this.CD.detectChanges();
  }

  /**
   * Ölçüm seçimini toggle et
   */
  secimToggle(uuid: string): void {
    if (this.seciliOlcumler.has(uuid)) {
      this.seciliOlcumler.delete(uuid);
    } else {
      this.seciliOlcumler.add(uuid);
    }
    this.CD.detectChanges();
  }

  /**
   * Tümünü seç/kaldır
   */
  tumunuSec(): void {
    if (this.seciliOlcumler.size === this.filtrelenmisListe.length) {
      this.seciliOlcumler.clear();
    } else {
      this.filtrelenmisListe.forEach(item => this.seciliOlcumler.add(item.uuid));
    }
    this.CD.detectChanges();
  }

  /**
   * Ölçümü sil
   */
  olcumSil(item: OlcumItem): void {
    if (!confirm(`"${item.baslik}" ölçümünü silmek istediğinizden emin misiniz?`)) {
      return;
    }

    this.kaliciOlcumler.olcumSil(item.grup);
    this.olcumleriYukle();
    this.sahneYenile();
  }

  /**
   * Seçili ölçümleri sil
   */
  secilileriSil(): void {
    if (this.seciliOlcumler.size === 0) return;

    if (!confirm(`${this.seciliOlcumler.size} ölçümü silmek istediğinizden emin misiniz?`)) {
      return;
    }

    this.seciliOlcumler.forEach(uuid => {
      const item = this.olcumListesi.find(o => o.uuid === uuid);
      if (item) {
        this.kaliciOlcumler.olcumSil(item.grup);
      }
    });

    this.seciliOlcumler.clear();
    this.olcumleriYukle();
    this.sahneYenile();
  }

  /**
   * Tüm ölçümleri sil
   */
  tumunuSil(): void {
    if (this.olcumListesi.length === 0) return;

    if (!confirm(`Tüm kalıcı ölçümleri (${this.olcumListesi.length} adet) silmek istediğinizden emin misiniz?`)) {
      return;
    }

    this.kaliciOlcumler.temizle();
    this.seciliOlcumler.clear();
    this.olcumleriYukle();
    this.sahneYenile();
  }

  /**
   * Ölçüm listesini CSV olarak dışa aktar (QC dokümantasyonu).
   * Seçili varsa sadece seçilileri, yoksa filtrelenmiş listeyi aktarır.
   */
  disariAktar(): void {
    const kaynak = this.seciliOlcumler.size > 0
      ? this.olcumListesi.filter(o => this.seciliOlcumler.has(o.uuid))
      : this.filtrelenmisListe;
    if (!kaynak.length) return;

    const kacir = (s: string) => `"${(s ?? '').toString().replace(/"/g, '""')}"`;
    const sb: string[] = ['Sıra;İsim;Değer / Başlık;Tarih;Görünür'];
    kaynak.forEach((o, i) => {
      const tarih = new Date(o.tarih).toLocaleString('tr-TR');
      sb.push([i + 1, kacir(o.isim), kacir(o.baslik), kacir(tarih), o.gorunur ? 'Evet' : 'Hayır'].join(';'));
    });

    const blob = new Blob(['﻿' + sb.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `olcumler-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Ölçüm görünürlüğünü toggle et
   */
  gorunurlukToggle(item: OlcumItem): void {
    item.grup.visible = !item.grup.visible;
    item.gorunur = item.grup.visible;
    this.sahneYenile();
    this.CD.detectChanges();
  }

  /**
   * Ölçümü 3D'de odakla
   */
  olcumuOdakla(item: OlcumItem): void {
    if (!this.anaGosterim) return;

    // Bounding box hesapla
    const box = new THREE.Box3().setFromObject(item.grup);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());

    // Kamerayı odakla
    if (this.anaGosterim.aktifKontrol) {
      this.anaGosterim.aktifKontrol.target.copy(center);
      this.anaGosterim.aktifKontrol.update();
    }

    this.sahneYenile();
  }

  /**
   * Ölçüm rengini değiştir
   */
  renkDegistir(item: OlcumItem, event: Event): void {
    const input = event.target as HTMLInputElement;
    const yeniRenk = new THREE.Color(input.value);

    item.grup.traverse((child) => {
      if (child instanceof THREE.Line) {
        const material = child.material as THREE.LineBasicMaterial;
        if (material && material.color) {
          material.color.copy(yeniRenk);
          material.needsUpdate = true;
        }
      }
      if (child instanceof THREE.Mesh) {
        const material = child.material as THREE.MeshBasicMaterial;
        if (material && material.color) {
          material.color.copy(yeniRenk);
          material.needsUpdate = true;
        }
      }
    });

    item.renk = input.value;
    this.sahneYenile();
    this.CD.detectChanges();
  }

  /**
   * Sahneyi yenile
   */
  private sahneYenile(): void {
    if (this.anaGosterim) {
      this.anaGosterim.ciz();
    }
  }

  /**
   * Tarih formatla
   */
  tarihFormatla(tarih: Date): string {
    if (!tarih) return '';
    const d = new Date(tarih);
    return d.toLocaleDateString('tr-TR') + ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  }
}

/**
 * Ölçüm listesi item interface
 */
interface OlcumItem {
  uuid: string;
  isim: string;
  baslik: string;
  tarih: Date;
  gorunur: boolean;
  renk: string;
  grup: THREE.Group;
}
